import { getCurrentInstance, onMounted } from 'vue'

const GTM_LOADED_FLAG = '__gtmLoaded'
const GTM_SCHEDULED_FLAG = '__gtmScheduled'

type GTMWindow = Window & {
  [GTM_LOADED_FLAG]?: boolean
  [GTM_SCHEDULED_FLAG]?: boolean
  __lxPartnerLead?: boolean
  __lxPartnerLeadPending?: boolean
  __lxPartnerLeadDispatched?: boolean
  dataLayer?: Record<string, unknown>[]
  fbq?: (...args: unknown[]) => void
}

const isAlreadyLoaded = () => !!(window as GTMWindow)[GTM_LOADED_FLAG]

const markLoaded = () => {
  ;(window as GTMWindow)[GTM_LOADED_FLAG] = true
}

const ensureDataLayer = (): Record<string, unknown>[] => {
  const win = window as GTMWindow
  if (!win.dataLayer) {
    win.dataLayer = []
  }
  return win.dataLayer
}

const injectGTMHeadScript = (gtmId: string, nonce?: string) => {
  const dataLayer = ensureDataLayer()
  dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`
  if (nonce) script.setAttribute('nonce', nonce)

  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.head.appendChild(script)
  }
}

const injectGTMBodyNoscript = (gtmId: string) => {
  const noscript = document.createElement('noscript')
  const iframe = document.createElement('iframe')
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`
  iframe.height = '0'
  iframe.width = '0'
  iframe.style.display = 'none'
  iframe.style.visibility = 'hidden'
  noscript.appendChild(iframe)

  document.body.insertBefore(noscript, document.body.firstChild)
}

const injectGTM = (gtmId: string, nonce?: string, skipBodyNoscript = false) => {
  if (isAlreadyLoaded()) return
  markLoaded()
  injectGTMHeadScript(gtmId, nonce)
  if (!skipBodyNoscript) injectGTMBodyNoscript(gtmId)
}

const setupEarlyPartnerCtaGuard = (triggerLoad: () => void) => {
  const win = window as GTMWindow
  document.addEventListener(
    'click',
    event => {
      if (win.__lxPartnerLead) return
      if (!/\/partners\/?$/.test(window.location.pathname)) return
      const target = event.target as Element | null
      const btn =
        target &&
        typeof target.closest === 'function' &&
        target.closest('.for-lawyers-hero__action--primary, .for-lawyers-cta__button')
      if (!btn) return

      win.__lxPartnerLeadPending = true
      const dataLayer = ensureDataLayer()
      dataLayer.push({
        event: 'partner_cta',
        content_name: 'partner_cta',
      })
      triggerLoad()

      let attempts = 0
      const timer = window.setInterval(() => {
        attempts += 1
        if (typeof win.fbq === 'function') {
          win.fbq('track', 'Lead', { content_name: 'partner_cta' })
          win.__lxPartnerLeadDispatched = true
          window.clearInterval(timer)
        } else if (attempts >= 30) {
          win.__lxPartnerLeadDispatched = true
          window.clearInterval(timer)
        }
      }, 25)
    },
    true
  )
}

const scheduleDeferredGTM = (
  gtmId: string,
  nonce?: string,
  skipBodyNoscript = false
) => {
  const win = window as GTMWindow
  ensureDataLayer()
  if (isAlreadyLoaded() || win[GTM_SCHEDULED_FLAG]) return
  win[GTM_SCHEDULED_FLAG] = true

  const events = [
    'pointerdown',
    'touchstart',
    'keydown',
    'scroll',
    'wheel',
    'click',
  ] as const

  const trigger = () => {
    for (const evt of events) {
      window.removeEventListener(evt, trigger, { capture: true })
    }
    injectGTM(gtmId, nonce, skipBodyNoscript)
  }

  for (const evt of events) {
    window.addEventListener(evt, trigger, {
      once: true,
      passive: true,
      capture: true,
    })
  }

  setupEarlyPartnerCtaGuard(trigger)
}

type UseLoadGTMScriptOptions = {
  nonce?: string
  skipBodyNoscript?: boolean
  deferUntilInteraction?: boolean
}

export const useLoadGTMScript = (
  gtmId: string,
  options: UseLoadGTMScriptOptions = {}
) => {
  if (typeof window === 'undefined') return

  const run = () => {
    if (options.deferUntilInteraction) {
      scheduleDeferredGTM(gtmId, options.nonce, options.skipBodyNoscript)
    } else {
      injectGTM(gtmId, options.nonce, options.skipBodyNoscript)
    }
  }

  if (getCurrentInstance()) {
    onMounted(run)
  } else {
    run()
  }
}
